import React from 'react';
import { useTheme } from '../../hooks/useTheme';
import { X } from 'lucide-react';

/**
 * Modal - Componente de diálogo modal reutilizable
 * 
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.isOpen - Estado de apertura del modal
 * @param {Function} props.onClose - Función para cerrar el modal
 * @param {string|React.ReactNode} props.title - Título del modal
 * @param {string|React.ReactNode} props.subtitle - Subtítulo del modal
 * @param {boolean} props.showCloseButton - Muestra el botón X para cerrar (default true)
 * @param {React.ReactNode} props.headerActions - Acciones adicionales en el encabezado (antes del cierre)
 * @param {React.ReactNode} props.footer - Franja inferior fija (no scrollea con el contenido)
 * @param {React.ReactNode} props.children - Contenido del modal
 * @param {string} props.size - Tamaño del modal (sm, md, lg, xl)
 * @param {boolean} props.isDarkMode - Modo oscuro
 * @param {boolean} props.closeOnOverlayClick - Si se puede cerrar haciendo click fuera del modal (default false)
 * @param {boolean} props.closeOnEscape - Si se puede cerrar con la tecla Escape
 * @param {string} props.className - Clases CSS adicionales
 * @param {Object} props.style - Estilos adicionales
 */
const Modal = ({
  isOpen = false,
  onClose,
  title = "",
  subtitle = "",
  children,
  size = "md",
  isDarkMode = false,
  closeOnOverlayClick = false,
  closeOnEscape = true,
  showCloseButton = true,
  headerActions = null,
  footer = null,
  className = "",
  style = {},
  edgeControl = null,
  sidePanel = null,
  sidePanelWidth = 0,
  ...props
}) => {
  const theme = useTheme();

  // Tamaños del modal
  const sizeStyles = {
    sm: { width: '400px', maxWidth: '90vw' },
    md: { width: '600px', maxWidth: '90vw' },
    lg: { width: '800px', maxWidth: '95vw' },
    xl: { width: '1000px', maxWidth: '95vw' },
    xxl: { width: '1440px', maxWidth: '98vw' },
  };

  // Estilos del modal
  const overlayStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: isOpen ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    // Por encima del sidebar (fixed ~1030 / toggle ~1050).
    zIndex: (theme.zIndex?.modal || 1040) + 80,
    padding: theme.spacing.md
  };

  const {
    overflow: _ignoredOverflow,
    maxHeight: styleMaxHeight,
    width: styleWidth,
    maxWidth: styleMaxWidth,
    ...restStyle
  } = style || {};

  const modalStyles = {
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.lg,
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    // El shell externo permite edgeControl; este box interno debe clippear y scrollear.
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    minHeight: 0,
    maxHeight: styleMaxHeight || '90vh',
    ...sizeStyles[size],
    ...restStyle,
  };

  const shellStyles = {
    position: 'relative',
    maxHeight: styleMaxHeight || '90vh',
    width: styleWidth || sizeStyles[size]?.width,
    maxWidth: styleMaxWidth || sizeStyles[size]?.maxWidth,
    // Visible para que edgeControl pueda sobresalir del borde del modal.
    overflow: 'visible',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  };

  const headerStyles = {
    padding: theme.spacing.lg,
    borderBottom: `1px solid ${theme.colors.border.primary}`,
    backgroundColor: theme.colors.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0
  };

  const titleStyles = {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.text.primary,
    margin: 0
  };

  const subtitleStyles = {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.text.secondary,
    margin: '4px 0 0 0'
  };

  const closeButtonStyles = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.sm,
    color: theme.colors.text.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease-in-out'
  };

  const contentStyles = {
    padding: theme.spacing.lg,
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  };

  const footerStyles = {
    flexShrink: 0,
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    borderTop: `1px solid ${theme.colors.border.primary}`,
    backgroundColor: theme.colors.secondary,
  };

  const mainColumn = (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          ...contentStyles,
          padding: sidePanel ? theme.spacing.lg : contentStyles.padding,
        }}
      >
        {children}
      </div>
      {footer ? <div style={footerStyles}>{footer}</div> : null}
    </div>
  );

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (closeOnEscape && e.key === 'Escape') {
      onClose();
    }
  };

  // Agregar event listener para ESC cuando el modal está abierto
  React.useEffect(() => {
    if (isOpen) {
      if (closeOnEscape) {
        document.addEventListener('keydown', handleKeyDown);
      }
      document.body.style.overflow = 'hidden'; // Prevenir scroll del body
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      if (closeOnEscape) {
        document.removeEventListener('keydown', handleKeyDown);
      }
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, closeOnEscape, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div 
      style={overlayStyles} 
      onClick={handleOverlayClick}
      className={className}
      {...props}
    >
      <div style={shellStyles}>
      <div style={{ ...modalStyles, width: '100%', maxWidth: '100%' }}>
        {/* Header del Modal */}
        <div style={headerStyles}>
          <div style={{ minWidth: 0, flex: 1 }}>
            {title ? <h2 style={titleStyles}>{title}</h2> : null}
            {subtitle ? <p style={subtitleStyles}>{subtitle}</p> : null}
          </div>
          {headerActions ? (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: theme.spacing.sm,
                flexShrink: 0,
                marginLeft: theme.spacing.md,
              }}
            >
              {headerActions}
            </div>
          ) : null}
          {showCloseButton ? (
          <button
            style={closeButtonStyles}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.hover;
              e.target.style.color = theme.colors.text.primary;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = theme.colors.text.secondary;
            }}
            aria-label="Cerrar modal"
          >
            <X size={20} />
          </button>
          ) : null}
        </div>

        {/* Contenido del Modal */}
        <div
          style={{
            display: 'flex',
            padding: sidePanel ? 0 : 0,
            overflow: 'hidden',
            flex: 1,
            minHeight: 0,
          }}
        >
          {mainColumn}
          {sidePanel ? (
            <aside
              style={{
                width: sidePanelWidth || 400,
                flexShrink: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                alignSelf: 'stretch',
              }}
            >
              {sidePanel}
            </aside>
          ) : null}
        </div>
      </div>

        {edgeControl || null}
      </div>
    </div>
  );
};

export default Modal;
